import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useContext, useRef } from 'react';
import { useItemList } from 'use-item-list';
import { KEY } from 'utils/ui/constants';
import useEventListener from 'hooks/useEventListener';
import usePopover from 'hooks/usePopover';
import usePopoverState from 'hooks/usePopoverState';
import useOnNavigateAway from 'hooks/useOnNavigateAway';
import useUniqueId from 'hooks/useUniqueId';
var NavMenuContext = /*#__PURE__*/createContext(null);
export function useNavMenuContext() {
  var navMenuContext = useContext(NavMenuContext);
  if (navMenuContext === null) {
    throw new Error('useNavMenuContext must be used within a NavMenu component');
  }
  return navMenuContext;
}
function NavMenu(_ref) {
  var idProp = _ref.id,
    _ref$menuPlacement = _ref.menuPlacement,
    menuPlacement = _ref$menuPlacement === void 0 ? 'bottom' : _ref$menuPlacement,
    menuDistance = _ref.menuDistance,
    menuPositionFixed = _ref.menuPositionFixed,
    children = _ref.children;
  var buttonRef = useRef(null);
  var popoverRef = useRef(null);
  var preventFocusResetOnClose = useRef(false);
  var id = useUniqueId(idProp);
  var popover = usePopover({
    ref: popoverRef,
    referenceRef: buttonRef,
    placement: menuPlacement,
    distance: menuDistance,
    positionFixed: menuPositionFixed,
    adaptivePositioning: true
  });
  var itemList = useItemList({
    id: id,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    onSelect: onSelect,
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    onHighlight: onHighlight,
    initialHighlightedIndex: undefined
  });
  var _usePopoverState = usePopoverState({
      onClose: function onClose() {
        var _popoverRef$current;
        itemList.clearHighlightedItem();
        var isFocusInPopover = popoverRef.current === document.activeElement || ((_popoverRef$current = popoverRef.current) === null || _popoverRef$current === void 0 ? void 0 : _popoverRef$current.contains(document.activeElement));

        // Move focus back to the button if it's within the popover
        // and if it's no link. Otherwise, preserve user focus.
        if (!preventFocusResetOnClose.current && isFocusInPopover) {
          var _buttonRef$current;
          (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.focus();
        }
        preventFocusResetOnClose.current = false;
      }
    }),
    isOpen = _usePopoverState.isOpen,
    open = _usePopoverState.open,
    close = _usePopoverState.close,
    toggle = _usePopoverState.toggle;
  function onHighlight(item) {
    var _item$ref$current;
    item === null || item === void 0 ? void 0 : (_item$ref$current = item.ref.current) === null || _item$ref$current === void 0 ? void 0 : _item$ref$current.focus();
  }
  function onSelect(item) {
    var _item$ref$current2;
    // When the selected item is a link, we don't want
    // to reset focus when the menu is closed, as focus should be managed
    // globally on navigation, e.g. using `components/PageTitleAnnouncer`.
    if (item !== null && item !== void 0 && (_item$ref$current2 = item.ref.current) !== null && _item$ref$current2 !== void 0 && _item$ref$current2.href) {
      preventFocusResetOnClose.current = true;
    }
    close();
  }
  function handleGlobalMenuKeyEvents(event) {
    var _popoverRef$current2;
    if (event.key === KEY.ESC) {
      close();
    }
    var isFocusInMenuOrOnButton = buttonRef.current === document.activeElement || popoverRef.current === document.activeElement || ((_popoverRef$current2 = popoverRef.current) === null || _popoverRef$current2 === void 0 ? void 0 : _popoverRef$current2.contains(document.activeElement));
    if (isFocusInMenuOrOnButton) {
      if (event.key === KEY.ARROW_UP) {
        event.preventDefault();
        itemList.moveHighlightedItem(-1);
      }
      if (event.key === KEY.ARROW_DOWN) {
        event.preventDefault();
        itemList.moveHighlightedItem(1);
      }
      if (event.key === KEY.SPACE || event.key === KEY.ENTER) {
        var item = itemList.getHighlightedItem();
        if (item) {
          event.preventDefault();
          if (!event.ctrlKey && !event.metaKey) {
            var _item$ref$current3;
            (_item$ref$current3 = item.ref.current) === null || _item$ref$current3 === void 0 ? void 0 : _item$ref$current3.click();
          }
          itemList.selectHighlightedItem();
        }
      }
      itemList.highlightItemByString(event);
    }
  }
  function handleButtonKeyEvents(event) {
    if (event.key === KEY.ARROW_DOWN) {
      event.preventDefault();
      open();
    }
  }

  // Handle global keyboard events when the menu is open
  useEventListener('keydown', handleGlobalMenuKeyEvents, {
    isEnabled: isOpen
  });

  // Close the menu when clicking outside of it,
  // otherwise just clear the menu highlighting
  useOnNavigateAway(popoverRef, function (event) {
    if (event.type === 'click') {
      close();
    }
    itemList.clearHighlightedItem();
  }, isOpen);
  var menuButtonProps = {
    ref: popover.setReferenceRef,
    id: itemList.controllerId,
    'aria-haspopup': 'menu',
    'aria-controls': itemList.listId,
    'aria-expanded': isOpen ? 'true' : 'false',
    onKeyDown: handleButtonKeyEvents,
    onClick: toggle
  };
  return /*#__PURE__*/React.createElement(NavMenuContext.Provider, {
    value: {
      popover: _objectSpread(_objectSpread({}, popover), {}, {
        isOpen: isOpen,
        toggle: toggle,
        open: open,
        close: close
      }),
      menuButtonProps: menuButtonProps,
      itemList: itemList
    }
  }, children);
}
export default NavMenu;